import { useState, useEffect, useCallback, useRef } from 'react';
import {
  ZebraPrinter,
  getAvailablePrinters,
  checkBrowserPrintStatus,
  printZpl,
  printTestLabel,
  PrintResult,
} from '../services/zebraPrinter.service';
import { useUserPreferencesStore } from '../stores/userPreferencesStore';
import { usePharmacyStore } from '../stores/pharmacyStore';

export type PrintMode = 'browser' | 'zebra';
export type LabelType = 'vial' | 'receipt' | 'delivery';
export type LabelOrientation = 'normal' | 'rotated90' | 'rotated180' | 'rotated270';

export interface PrinterAssignments {
  vial: ZebraPrinter | null;
  receipt: ZebraPrinter | null;
  delivery: ZebraPrinter | null;
}

export interface OrientationSettings {
  vial: LabelOrientation;
  receipt: LabelOrientation;
  delivery: LabelOrientation;
}

export interface ZebraPrinterConfig {
  enabled: boolean;
  printers: PrinterAssignments;
  orientations: OrientationSettings;
}

const STORAGE_KEY = 'zebraPrinterConfig';

// Bounded retry for a failed server save: up to 2 retries (3 attempts total)
// with exponential backoff, then give up. See the save-effect comment below
// for why this is keyed on save FAILURE, never on `preferences` identity.
const MAX_SAVE_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 2000;

const defaultConfig: ZebraPrinterConfig = {
  enabled: true,  // Default to enabled
  printers: {
    vial: null,
    receipt: null,
    delivery: null,
  },
  orientations: {
    vial: 'normal',
    receipt: 'normal',
    delivery: 'normal',
  },
};

/**
 * Merge the server-side printer settings into this station's local config on
 * the one-time initial sync.
 *
 * The whole config is a per-STATION hardware fact — Zebra Browser Print and
 * the physical printers live on this machine — while the roaming server copy
 * is routinely stale: the debounced preference save silently skips when prefs
 * aren't loaded yet. On release nights every long-lived pharmacy tab reloads
 * at once and the stale server copy wiped stations (2026-07-21 release: 146
 * of 176 prod printer_settings rows said enabled=false, and default rows with
 * all-null printers erased real assignments, then got cached back over the
 * good localStorage copy). The rule: the server may only ADD to a station,
 * never take away —
 *  - `enabled`: a locally-enabled station is never disabled by the sync;
 *  - printers/orientations: per label type, a slot this station has
 *    configured keeps its local printer AND orientation; the server fills
 *    only empty slots (roaming onto fresh stations still works).
 * The save-back effect then repairs the stale server copy from local truth.
 */
export function mergeServerPrinterConfig(
  local: ZebraPrinterConfig,
  server: Partial<ZebraPrinterConfig> | undefined,
): ZebraPrinterConfig {
  const labelTypes: LabelType[] = ['vial', 'receipt', 'delivery'];
  const printers = { ...defaultConfig.printers };
  const orientations = { ...defaultConfig.orientations };
  for (const type of labelTypes) {
    const localPrinter = local.printers?.[type] ?? null;
    if (localPrinter) {
      printers[type] = localPrinter;
      orientations[type] = local.orientations?.[type] ?? 'normal';
    } else {
      printers[type] = server?.printers?.[type] ?? null;
      orientations[type] =
        server?.orientations?.[type] ?? local.orientations?.[type] ?? 'normal';
    }
  }
  return {
    enabled: local.enabled || (server?.enabled ?? false),
    printers,
    orientations,
  };
}

/**
 * Hook for managing Zebra Browser Print printers
 * Settings are synced with user preferences (per pharmacy) and cached in localStorage
 */
export function useZebraPrinters() {
  const [availablePrinters, setAvailablePrinters] = useState<ZebraPrinter[]>([]);

  // Load from localStorage synchronously on first render
  const [config, setConfig] = useState<ZebraPrinterConfig>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as ZebraPrinterConfig;
        console.log('[ZebraPrint] Initial load from localStorage:', parsed);
        return parsed;
      }
    } catch (e) {
      console.error('[ZebraPrint] Failed to load initial config:', e);
    }
    return defaultConfig;
  });

  const [browserPrintAvailable, setBrowserPrintAvailable] = useState<boolean | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get pharmacy and user preferences
  const { activePharmacy } = usePharmacyStore();
  const { preferences, updatePrinterSettings } = useUserPreferencesStore();
  const pharmacyId = activePharmacy?.id;

  // Track if we've initialized from preferences to avoid loops
  const initializedFromPrefs = useRef(true); // Already loaded from localStorage in useState
  const savingToPrefs = useRef(false);

  // Sync with user preferences if available (secondary)
  // NOTE: Only sync from preferences on FIRST load, not on every preferences change
  // This prevents infinite loop: config change -> updatePrefs -> prefs change -> config change
  const hasLoadedFromPrefs = useRef(false);
  useEffect(() => {
    // Only load from preferences ONCE when they first become available
    if (preferences?.printerSettings && !hasLoadedFromPrefs.current && !savingToPrefs.current) {
      console.log('[ZebraPrint] Initial sync from user preferences:', preferences.printerSettings);
      const printerSettings = preferences.printerSettings as any;
      // A locally-enabled station is never switched off by the server copy —
      // see mergeServerPrinterConfig. Functional update so the merge sees the
      // real current local config, not a stale closure.
      setConfig((prev) => {
        const newConfig = mergeServerPrinterConfig(prev, printerSettings);
        // Also save to localStorage for next time (idempotent if re-run).
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(newConfig));
        } catch (e) {
          console.error('[ZebraPrint] Failed to cache merged config:', e);
        }
        return newConfig;
      });
      hasLoadedFromPrefs.current = true;
      initializedFromPrefs.current = true;
    }
  }, [preferences?.printerSettings]);

  // Track last saved config to avoid redundant saves
  const lastSavedConfig = useRef<string | null>(null);

  // Pending retry timer + attempt count for a failed server save (see
  // runSaveAttempt below). Reset on success, on give-up, and whenever the
  // effect's cleanup fires for a newer config.
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryAttemptRef = useRef(0);

  // PHOX-GSC-FE-2Y: keep the latest preferences/updatePrinterSettings available
  // to the save effect WITHOUT them being dependencies of it. This hook mounts
  // TWICE at once (here and in LabelPrintingModal); a successful save from one
  // instance calls updatePrinterSettings, which replaces the store's shared
  // `preferences` object, which — when `preferences` was a dep — re-ran BOTH
  // instances' save effects purely because the store echoed back, chaining an
  // extra commit onto every save (and onto every OTHER preference change,
  // whether or not it had anything to do with printers). Reading through refs
  // means only an actual `config` change schedules a save.
  const preferencesRef = useRef(preferences);
  const updatePrinterSettingsRef = useRef(updatePrinterSettings);
  useEffect(() => {
    preferencesRef.current = preferences;
    updatePrinterSettingsRef.current = updatePrinterSettings;
  }, [preferences, updatePrinterSettings]);

  // Freshest config/pharmacyId, readable from callbacks that fire well after
  // the effect run that scheduled them (retries, and the flush paths below)
  // without needing to re-run any effect.
  const configRef = useRef(config);
  const pharmacyIdRef = useRef(pharmacyId);
  useEffect(() => {
    configRef.current = config;
    pharmacyIdRef.current = pharmacyId;
  }, [config, pharmacyId]);

  // Whether this hook instance is still mounted. Checked before every write
  // and before scheduling every retry so nothing fires — and nothing writes
  // — after teardown (M1).
  const hookLiveRef = useRef(true);
  useEffect(() => {
    hookLiveRef.current = true;
    return () => {
      hookLiveRef.current = false;
    };
  }, []);

  // A save attempt that was skipped because it was blocked (preferences not
  // loaded yet, or another save already in flight) records itself here so
  // the newest config still reaches the server once unblocked, instead of
  // being dropped silently. See runSaveAttempt below (H1 / H2).
  const pendingFlushRef = useRef<{ pharmacyId: string } | null>(null);

  // Attempt (or retry) a server save. Always sends the FRESHEST config this
  // hook currently holds (via configRef), never a value captured back when
  // this particular attempt/retry was scheduled — that's what lets a flush
  // triggered long after the fact still carry the newest assignment.
  //
  // attempt 1 is the initial debounced save; attempt > 1 is a retry after a
  // prior failure. `snapshotBeforeAttempt` is the server-known printerSettings
  // JSON captured right before the PREVIOUS attempt ran — see the staleness
  // check below for why a retry needs it instead of just re-sending `config`.
  const runSaveAttempt = async (
    ownerPharmacyId: string | undefined,
    attempt: number,
    snapshotBeforeAttempt: string | null,
  ) => {
    // The hook has unmounted, or moved on to a different pharmacy, since
    // this attempt was scheduled — never write, and never chain a further
    // retry or flush, for a pharmacy/config we've left (M1).
    if (!hookLiveRef.current || !ownerPharmacyId || ownerPharmacyId !== pharmacyIdRef.current) {
      return;
    }

    // Only save if preferences are loaded (updatePrinterSettings requires
    // this) and no other save is already in flight. Read the latest values
    // via ref — see comment above.
    const currentPreferences = preferencesRef.current;
    const currentUpdatePrinterSettings = updatePrinterSettingsRef.current;
    if (!currentPreferences || savingToPrefs.current) {
      // Blocked. Record that this pharmacy still has an unsent config so
      // whichever unblocks first — the in-flight save settling (H1), or
      // preferences finishing their first load (H2) — flushes the CURRENT
      // config once it's safe to, instead of the change vanishing because
      // `preferences` is deliberately not a dependency of the save effect
      // (see PHOX-GSC-FE-2Y above).
      pendingFlushRef.current = { pharmacyId: ownerPharmacyId };
      return;
    }

    const configToSend = configRef.current;
    const configJson = JSON.stringify(configToSend);
    if (configJson === lastSavedConfig.current) {
      return;
    }

    const snapshotNow = JSON.stringify(currentPreferences.printerSettings ?? null);
    if (attempt > 1 && snapshotNow !== snapshotBeforeAttempt) {
      // The server-known printer settings moved since our last failed
      // attempt — another mounted hook instance in THIS tab already saved a
      // fresher value for this station. (Same-tab only: this reads through
      // this store's own `preferences`, which only moves when a write lands
      // via this tab's zustand instance — a different browser tab is
      // invisible to it. See the retryOnFailure spec header for what this
      // guard does and doesn't cover.) Back off instead of clobbering it
      // with a stale resend (PHOX-GSC-FE-2Y).
      console.log('[ZebraPrint] Skipping retry: printer settings changed on the server since last attempt');
      retryAttemptRef.current = 0;
      return;
    }

    try {
      savingToPrefs.current = true;
      console.log(
        '[ZebraPrint] Saving to user preferences:',
        configToSend,
        attempt > 1 ? `(retry ${attempt - 1}/${MAX_SAVE_ATTEMPTS - 1})` : '',
      );
      await currentUpdatePrinterSettings(ownerPharmacyId, {
        enabled: configToSend.enabled,
        printers: configToSend.printers,
        orientations: configToSend.orientations,
      } as any);
      lastSavedConfig.current = configJson;
      retryAttemptRef.current = 0;
    } catch (e) {
      console.error('[ZebraPrint] Failed to save to user preferences:', e);
      if (!hookLiveRef.current || ownerPharmacyId !== pharmacyIdRef.current) {
        // Moved on since the request went out — never schedule a retry for
        // a pharmacy/config we've left (M1).
        retryAttemptRef.current = 0;
      } else if (attempt < MAX_SAVE_ATTEMPTS) {
        const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
        retryAttemptRef.current = attempt;
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
        }
        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null;
          runSaveAttempt(ownerPharmacyId, attempt + 1, snapshotNow);
        }, delay);
      } else {
        console.error('[ZebraPrint] Giving up saving printer settings after', MAX_SAVE_ATTEMPTS, 'attempts');
        retryAttemptRef.current = 0;
      }
    } finally {
      savingToPrefs.current = false;
      // Now that this save has settled (success or failure), flush whatever
      // config change was skipped while it was in flight (H1) — always the
      // CURRENT config, never a captured stale one, and only for the
      // pharmacy we're still on.
      if (
        hookLiveRef.current &&
        pendingFlushRef.current &&
        pendingFlushRef.current.pharmacyId === pharmacyIdRef.current
      ) {
        const pending = pendingFlushRef.current;
        pendingFlushRef.current = null;
        runSaveAttempt(pending.pharmacyId, 1, null);
      }
    }
  };

  // Once user preferences finish their first load, flush any save that was
  // skipped while they were still loading (H2). When the loaded row DOES
  // have printerSettings, the sync-from-prefs effect above already changes
  // `config` and that alone re-triggers the debounced save below — flushing
  // here too would just send an earlier, redundant duplicate of the same
  // call. So this only acts on the case that effect can't reach: a server
  // row with no printerSettings at all, which never makes `config` change.
  const hasFlushedOnPrefsLoad = useRef(false);
  useEffect(() => {
    if (!preferences || hasFlushedOnPrefsLoad.current) return;
    hasFlushedOnPrefsLoad.current = true;
    const pending = pendingFlushRef.current;
    if (!pending || pending.pharmacyId !== pharmacyIdRef.current) return;
    if ((preferences as any)?.printerSettings) {
      // The sync-from-prefs effect above changes `config` for this exact
      // transition, which alone re-triggers an ordinary debounced save.
      // Consume the pending marker here so it can't ALSO be replayed later
      // (e.g. from a save's `finally` block once some other attempt
      // settles) and fire a redundant extra PUT.
      pendingFlushRef.current = null;
      return;
    }
    pendingFlushRef.current = null;
    runSaveAttempt(pending.pharmacyId, 1, null);
  }, [preferences]);

  // Save config to user preferences when it changes (user-initiated changes only)
  useEffect(() => {
    // Skip if we haven't initialized yet
    if (!initializedFromPrefs.current) return;

    // Serialize config for comparison
    const configJson = JSON.stringify(config);

    // Skip if config hasn't actually changed (prevents save after loading from prefs)
    if (configJson === lastSavedConfig.current) {
      console.log('[ZebraPrint] Config unchanged, skipping save');
      return;
    }

    // Also save to localStorage as backup/cache.
    // Deliberately does NOT stamp lastSavedConfig: that stamp means "the
    // SERVER has this copy". Stamping here made a skipped API save (prefs
    // not loaded yet) look already-saved forever, so the server row stayed
    // stale and wiped stations on the next reload. The localStorage write is
    // idempotent, so re-running this effect until the API save succeeds is
    // harmless.
    try {
      console.log('[ZebraPrint] Saving to localStorage (cache):', configJson);
      localStorage.setItem(STORAGE_KEY, configJson);
    } catch (e) {
      console.error('[ZebraPrint] Failed to save printer config to localStorage:', e);
    }

    // Debounce saving to API - only save if config is different from last save
    const timeoutId = setTimeout(() => {
      runSaveAttempt(pharmacyId, 1, null);
    }, 1000);
    // Cancel BOTH the pending debounce and any in-flight retry when `config`
    // (or `pharmacyId`) changes again, or on unmount. Combined with the
    // pharmacy/liveness check at the top of runSaveAttempt, this guarantees
    // a retry can never fire for a config/pharmacy older than the newest one
    // this hook holds.
    return () => {
      clearTimeout(timeoutId);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      retryAttemptRef.current = 0;
    };
  }, [config, pharmacyId]);

  // Check Browser Print status on mount and set initialized flag
  useEffect(() => {
    checkBrowserPrintStatus().then((status) => {
      console.log('[ZebraPrint] Initial status check:', status);
      setBrowserPrintAvailable(status);
      setIsInitialized(true);
    });
  }, []);

  // Periodic refresh of Browser Print status (every 30 seconds when enabled)
  // This catches cases where Zebra Browser Print is started/stopped after page load
  useEffect(() => {
    if (!config.enabled) return;

    const interval = setInterval(async () => {
      try {
        const status = await checkBrowserPrintStatus();
        if (status !== browserPrintAvailable) {
          console.log('[ZebraPrint] Status changed:', browserPrintAvailable, '->', status);
          setBrowserPrintAvailable(status);
        }
      } catch (e) {
        // Silent fail on periodic check
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [config.enabled, browserPrintAvailable]);

  /**
   * Refresh the list of available printers
   */
  const loadPrinters = useCallback(async () => {
    console.log('[ZebraPrint] loadPrinters called');
    setLoading(true);
    setError(null);

    try {
      const status = await checkBrowserPrintStatus();
      console.log('[ZebraPrint] Browser Print status:', status);
      setBrowserPrintAvailable(status);

      if (!status) {
        // Not running - this is not an error, just unavailable
        console.log('[ZebraPrint] Browser Print not running');
        setAvailablePrinters([]);
        return [];
      }

      const printers = await getAvailablePrinters();
      console.log('[ZebraPrint] Discovered printers:', printers);
      setAvailablePrinters(printers);

      // Note: We no longer clear assignments if printers aren't found
      // This allows assignments to persist even when printers are temporarily unavailable

      return printers;
    } catch (e: any) {
      const errorMsg = e.message || 'Failed to load printers';
      console.warn('[ZebraPrint] loadPrinters:', errorMsg);
      setError(errorMsg);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Enable or disable Zebra printing mode
   */
  const setEnabled = useCallback((enabled: boolean) => {
    setConfig((prev) => ({ ...prev, enabled }));
  }, []);

  /**
   * Assign a printer to a label type
   */
  const assignPrinter = useCallback((labelType: LabelType, printer: ZebraPrinter | null) => {
    console.log('[ZebraPrint] Assigning printer to', labelType, ':', printer);
    setConfig((prev) => {
      const newConfig = {
        ...prev,
        printers: {
          ...prev.printers,
          [labelType]: printer,
        },
      };
      console.log('[ZebraPrint] New config after assignment:', newConfig);
      return newConfig;
    });
  }, []);

  /**
   * Set orientation for a label type
   */
  const setOrientation = useCallback((labelType: LabelType, orientation: LabelOrientation) => {
    console.log('[ZebraPrint] Setting orientation for', labelType, ':', orientation);
    setConfig((prev) => ({
      ...prev,
      orientations: {
        ...prev.orientations,
        [labelType]: orientation,
      },
    }));
  }, []);

  /**
   * Get orientation for a label type
   */
  const getOrientation = useCallback(
    (labelType: LabelType): LabelOrientation => {
      return config.orientations?.[labelType] ?? 'normal';
    },
    [config.orientations]
  );

  /**
   * Get the assigned printer for a label type
   */
  const getPrinter = useCallback(
    (labelType: LabelType): ZebraPrinter | null => {
      return config.printers[labelType];
    },
    [config.printers]
  );

  /**
   * Print ZPL to the assigned printer for a label type
   */
  const print = useCallback(
    async (labelType: LabelType, zplContent: string): Promise<PrintResult> => {
      const printer = config.printers[labelType];

      console.log('[ZebraPrint] Label type:', labelType);
      console.log('[ZebraPrint] Config printers:', config.printers);
      console.log('[ZebraPrint] Selected printer:', printer);

      if (!printer) {
        return {
          success: false,
          error: `No printer assigned for ${labelType} labels`,
        };
      }

      return printZpl(printer, zplContent);
    },
    [config.printers]
  );

  /**
   * Print a test label to the assigned printer
   */
  const testPrint = useCallback(
    async (labelType: LabelType): Promise<PrintResult> => {
      const printer = config.printers[labelType];

      if (!printer) {
        return {
          success: false,
          error: `No printer assigned for ${labelType} labels`,
        };
      }

      return printTestLabel(printer);
    },
    [config.printers]
  );

  /**
   * Check if Zebra printing is ready for a specific label type
   */
  const isReady = useCallback(
    (labelType: LabelType): boolean => {
      return config.enabled && browserPrintAvailable === true && config.printers[labelType] !== null;
    },
    [config.enabled, browserPrintAvailable, config.printers]
  );

  /**
   * Wait for initialization to complete, then check if Zebra is ready.
   * Use this before printing to avoid race conditions during startup.
   * @param labelType - The type of label to check readiness for
   * @param timeoutMs - Maximum time to wait for initialization (default 3000ms)
   * @returns Promise<boolean> - Whether Zebra is ready after initialization
   */
  const waitForReady = useCallback(
    async (labelType: LabelType, timeoutMs = 3000): Promise<boolean> => {
      // If already initialized, return immediately
      if (isInitialized) {
        const ready = config.enabled && browserPrintAvailable === true && config.printers[labelType] !== null;
        if (!ready) {
          console.warn('[ZebraPrint] waitForReady: Not ready after init.', {
            enabled: config.enabled,
            browserPrintAvailable,
            printerAssigned: config.printers[labelType] !== null,
            labelType,
          });
        }
        return ready;
      }

      // Wait for initialization with timeout
      console.log('[ZebraPrint] waitForReady: Waiting for initialization...');
      const start = Date.now();
      while (!isInitialized && Date.now() - start < timeoutMs) {
        await new Promise((r) => setTimeout(r, 100));
      }

      if (!isInitialized) {
        console.warn('[ZebraPrint] waitForReady: Timed out waiting for initialization');
        return false;
      }

      const ready = config.enabled && browserPrintAvailable === true && config.printers[labelType] !== null;
      if (!ready) {
        console.warn('[ZebraPrint] waitForReady: Not ready after waiting.', {
          enabled: config.enabled,
          browserPrintAvailable,
          printerAssigned: config.printers[labelType] !== null,
          labelType,
        });
      }
      return ready;
    },
    [isInitialized, config.enabled, browserPrintAvailable, config.printers]
  );

  /**
   * Get the current print mode based on config and availability
   */
  const getPrintMode = useCallback((): PrintMode => {
    if (config.enabled && browserPrintAvailable) {
      return 'zebra';
    }
    return 'browser';
  }, [config.enabled, browserPrintAvailable]);

  return {
    // State
    availablePrinters,
    printerAssignments: config.printers,
    orientations: config.orientations,
    enabled: config.enabled,
    browserPrintAvailable,
    isInitialized,
    loading,
    error,

    // Actions
    loadPrinters,
    setEnabled,
    assignPrinter,
    setOrientation,
    getPrinter,
    getOrientation,
    print,
    testPrint,

    // Helpers
    isReady,
    waitForReady,
    getPrintMode,
  };
}

export default useZebraPrinters;
